<template lang="html">
  <!-- callbacks drag events proxied  -->
  <section
    class="custom-display-element-config"
    :id="id"
    :draggable="draggable"
    @dragstart.stop="onComponentStartDrag($event)"
    @dragend.stop="onComponentEndDrag($event)"
    @dragover.prevent.stop
    @dragenter.stop="onComponentDragEnter($event)"
  >
    <div class="header">
      <!-- empty placehholder -->
      <div class="card-description">{{ widgetData.description }}</div>
      <settings-element
        :allow-edit="allowEdit"
        @on-settings-edit-click="editConfigElement"
        @on-settings-delete-click="onDeleteClick"
      />
    </div>

    <!-- content specific to this component -->
    <div v-if="isRequestingTranslations" class="loader"></div>
    <div v-else>
      <div v-if="hasWidgetAccess" class="main-content">
        <div class="widget-name">{{ widgetName }}</div>
        <div
          v-if="widgetVersions.length > 1 || isDowngradeVersion"
          class="slds-form-element"
        >
          <div class="slds-form-element__control">
            <div class="slds-select_container">
              <select
                id="select-widget-version"
                v-model="selectedVersion"
                @change.stop="onVersionSelected"
                class="slds-select"
                :disabled="displayLoader"
              >
                <option v-for="(version, index) in widgetVersions" :key="index">
                  {{ version }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div v-else>{{ selectedVersion }}</div>
        <div class="new-version" v-if="isUpgradeVersion">
          <svg @click.stop="onUpdateVersion">
            <use
              xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#level_up"
            />
          </svg>
        </div>
        <div v-if="!isAvailableVersion">
          {{ $t("unavailableCustomDisplayElement") }}
        </div>
        <div class="loader" v-if="displayLoader"></div>
      </div>
      <div v-else>{{ $t("unavailableCustomDisplayElement") }}</div>
    </div>

    <simple-modal
      :show="displaySimpleModal"
      :message="getMessageText(messages.ARE_YOU_SURE)"
      :cancel-text="getMessageText(messages.CANCEL)"
      :ok-text="getMessageText(messages.OK)"
      @on-simple-modal-cancel="onCancelDeleteElement"
      @on-simple-modal-ok="onConfirmDeleteElement"
    />
  </section>
</template>

<script lang="js">
  import { MUTATION_NAME, TOAST_OPTIONS } from "@/static/global-variables";
  import distributionChannelUtil from "@/utils/distribution-channel-util";
  import DistributionChannelsService from "@/services/distributionChannelsService";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import SettingsElement from "@/views/drag-drop-pages/components/SettingsElement";

  export default  {
    name: "custom-display-element-config",
    components: {
      SimpleModal,
      SettingsElement
    },
    props: {
      id: String,
      // props specific to draggable component
      allowDropzones: {
        type: Boolean,
        default: true
      },
      dropEffect: {
        type: String,
        default: dragAndDrop.DROP_EFFECT.MOVE
      },
      draggable: {
        type: Boolean,
        default: true
      },
      // props specific to this component
      // will be set when this config object is first added to the canvas
      uuid: String,
      version: String,
      versionUuid: String,
      description: String,
      properties: {
        type: Object,
        default: function() {
          return {};
        }
      }
    },
    data () {
      return {
        displaySimpleModal: false,
        allowEdit: false,
        selectedVersion: null,
        widgetVersionUuid: null,
        displayLoader: false
      }
    },
    computed: {
      appId() {
        return this.$route.params.appId;
      },
      userLocale() {
        const user = this.$store.getters.user;
        return user ? user.language : "en_US";
      },
      messages() {
        return this.$store.state.messages;
      },
      hasWidgetAccess() {
        return Boolean(this.widgetData.widget);
      },
      widgetVersions() {
        return this.widgetData.widget? distributionChannelUtil.parseWidgetVersions(this.widgetData.widget.versions) : null;
      },
      isAvailableVersion() {
        return this.widgetVersions? this.widgetVersions.includes(this.selectedVersion) : false;
      },
      clonedWidget() {
        let widget;
        for(const channel of this.distributedChannelList) {
          const el = channel.widgets.find(e => e.widgetId === this.uuid);
          if(el) {
            widget = JSON.parse(JSON.stringify(el));
            break;
          }
        }
        return widget;
      },
      widgetData() {
        return {
          uuid: this.uuid,
          version: this.version,
          properties: this.properties,
          description: this.description,
          widget: this.clonedWidget
        };
      },
      distributedChannelList() {
        return this.$store.getters[MUTATION_NAME.DISTRIBUTION_CHANNEL_LIST] || [];
      },
      widgetName() {
        const widgetName = this.widgetData.widget? this.widgetData.widget.name : null;
        return distributionChannelUtil.findTranslatedLabel(this.translations, this.userLocale) || widgetName;
      },
      translations() {
        return this.allWidgetTranslations[this.uuid] || [];
      },
      allWidgetTranslations() {
        return this.$store.getters[MUTATION_NAME.ALL_WIDGET_TRANSLATIONS] || {};
      },
      isRequestingTranslations() {
        return this.$store.getters[MUTATION_NAME.WIDGET_TRANSLATION_REQUEST][this.uuid];
      },
      mostRecentVersion() {
        return this.widgetVersions? this.widgetVersions[0] : null;
      },
      semanticMostRecentVersion() {
        return distributionChannelUtil.splitSemanticVersion({
          version: this.mostRecentVersion,
        });
      },
      semanticCurrentRecentVersion() {
        return distributionChannelUtil.splitSemanticVersion({
          version: this.version
        });
      },
      isUpgradeVersion() {
        return distributionChannelUtil.isUpgradeVersion(this.semanticCurrentRecentVersion, this.semanticMostRecentVersion);
      },
      isDowngradeVersion() {
        return distributionChannelUtil.isUpgradeVersion(this.semanticMostRecentVersion, this.semanticCurrentRecentVersion);
      },
      widgetDetails() {
        return this.$store.getters[MUTATION_NAME.ALL_WIDGET_VERSION_DETAILS][this.widgetVersionUuid];
      }
    },
    watch: {
      uuid: function() {
        if(this.uuid) {
          this.getWidgetTranslations();
        }
      },
      widgetVersions: function() {
        if (this.widgetData.widget && Array.isArray(this.widgetData.widget.versions)) {
          const formattedVersions = distributionChannelUtil.formatAndSortSemanticVersion(this.widgetData.widget.versions);
          const ver = formattedVersions.find(v => v.uuid === this.versionUuid);
          if (ver) {
            this.updateVersionVariables(ver.version);
            if (ver.version !== this.version) {
              this.triggerCanvasUpdate();
            }
          } else {
            this.setSelectedVersion(this.version);
          }
        }

        this.updateAllowEditFlag();
      },
      version: function() {
        if(this.uuid && this.metaData) {
          // this is coming from edit modal
          this.updateVersionVariables(this.version)
        }
      },
      properties: {
        handler() {
          if(this.uuid && this.metaData) {
            // this is coming from edit modal
            this.metaData.properties = this.properties;
            this.metaData.description = this.description;
          }
        },
        deep: true,
        immediate: true
      }
    },
    methods: {
      // common methods
      onCancelDeleteElement() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteElement() {
        this.displaySimpleModal = false;
        const obj = {
          id: this.id
        };
        this.$store.commit(MUTATION_NAME.SET_DELETE_DRAGGABLE, { detail: obj });
      },
      onDeleteClick() {
        this.displaySimpleModal = true;
      },
      editConfigElement() {
        // object to pass on to edit modal
        const obj = {
          // required variables
          id: this.id,
          modal: "AddEditCustomDisplayElementModal",
          dropEffect: dragAndDrop.DROP_EFFECT.NONE,
          // specific to this component
          props: this.widgetData
        };
        this.$store.commit(MUTATION_NAME.SET_EDIT_DRAGGABLE, { detail: obj });
      },
      getMetadata() {
        if(!this.metaData) {
          const v = this.widgetData.widget.versions.find(e => e.version === this.version);
          const uuid = v ? v.uuid : null;
          this.metaData = {
            element: {
              name: this.$options.name // this field is required for all components, do not remove
            },
            type: "Custom",
            uuid: this.uuid,
            version: this.version,
            versionUuid: uuid,
            properties: this.properties,
            description: this.widgetData.description
          };
        }
        return this.metaData;
      },
      deserialize(serializedJson) {
        // serializedJson will contain metaData from getMetadata()
        // Called only:
        //  - when this element is created from the configuration file or
        //  - when is dragged to other location on the canvas
        this.metaData = serializedJson;
        this.version = serializedJson.version;
        this.versionUuid = serializedJson.versionUuid;
        this.properties = serializedJson.properties;
        this.uuid = serializedJson.uuid; // set uuid to trigger find widget object
        this.description = serializedJson.description;
      },

      // methods specific to this component
      getVersionDetailsAndDowngrade(widgetUuid, widgetVersionUuid) {
        this.displayLoader = true;
        DistributionChannelsService.getWidgetVersionDetails(widgetUuid, widgetVersionUuid)
        .then(() => {
          this.downgradeVersion();
        })
        .catch((e) => {
          this.selectedVersion = this.version;
          this.$bvToast.toast(this.getMessageText(this.messages.ERROR_WHILE_LOADING), TOAST_OPTIONS.bottomCenter);
          console.error(e);
        })
        .finally(() => {
          this.displayLoader = false;
        });
      },
      updateAllowEditFlag() {
        let found = false;
        if(this.widgetVersions) {
          found = this.widgetVersions.includes(this.widgetData.version);
        }
        this.allowEdit = this.hasWidgetAccess && found;
      },
      getWidgetTranslations() {
        if(!this.translations.length && !this.isRequestingTranslations) {
          this.setRequestFlag(true);
          DistributionChannelsService.getWidgetTranslations(this.uuid)
          .finally(() => {
            this.setRequestFlag(false);
          });
        }
      },
      setRequestFlag(isRequest) {
        this.$store.commit(MUTATION_NAME.WIDGET_TRANSLATION_REQUEST, {
          uuid: this.uuid,
          request: isRequest
        });
      },
      onVersionSelected() {
        const selectedSemanticVersion = distributionChannelUtil.splitSemanticVersion({
          version: this.selectedVersion
        });
        this.update(this.semanticCurrentRecentVersion, selectedSemanticVersion);
      },
      onUpdateVersion() {
        if(!this.displayLoader) {
          this.update(this.semanticCurrentRecentVersion, this.semanticMostRecentVersion);
        }
      },
      updateVersionVariables(version) {
        this.widgetData.version = version;
        this.metaData.version = version;
        if (this.widgetData.widget && this.widgetData.widget.versions) {
          const widgetVersion = this.widgetData.widget.versions.find(e => e.version === version);
          const uuid = widgetVersion ? widgetVersion.uuid : null;
          this.widgetData.versionUuid = uuid;
          this.metaData.versionUuid = uuid;
        }
        this.setSelectedVersion(version);
      },
      setSelectedVersion(version) {
        this.selectedVersion = version;
      },
      update(current, selected) {
        if(distributionChannelUtil.isShowEditModal(current, selected)) {
          // currentVersion and selectedVersion differ by minor version
          // currentVersion < selectedVersion   - E.g  1.0.0  < 1.2.0  or
          // currentVersion and selectedVersion differ by major version  -  E.g.  1.0.0 < 2.0.0
          //   - The edit modal for version selectedVersion pops up automatically.
          //   - Any inputs for properties that versions currentVersion and selectedVersion have in common
          //     are pre-populated with the existing values.
          //   - Any properties that currentVersion has that selectedVersion does not are dropped
          this.widgetData.newVersion = selected.version;
          this.widgetData.shouldUpdateProperties = true;
          this.editConfigElement();
        } else if(distributionChannelUtil.isDowngradeAndDropProperties(current, selected)) {
          // currentVersion and selectedVersion differ by minor version
          // currentVersion > selectedVersion   - E.g. 1.2.0  >  1.1.0
          //   - Version is updated immediately.
          //   - Any properties that currentVersion has that selectedVersion does not are dropped.
          const widgetVersion = this.widgetData.widget.versions.find(e => e.version === selected.version);
          this.widgetVersionUuid = widgetVersion.uuid;
          if(!this.widgetDetails) {
            this.getVersionDetailsAndDowngrade(this.widgetData.uuid, this.widgetVersionUuid);
          } else {
            this.downgradeVersion();
          }
        } else if(distributionChannelUtil.isUpdateImediately(current, selected)) {
          // currentVersion and selectedVersion only differ by
          // patch number	 - E.g.  1.1.0  to  1.1.1 or viceversa
          // Version is updated immediately
          this.updatePatchVersion(selected);
        }
      },
      updatePatchVersion(semanticVersion) {
        this.updateVersionVariables(semanticVersion.version);
        this.triggerCanvasUpdate();
      },
      downgradeVersion() {
        distributionChannelUtil.downgradeVersion(this.metaData, this.widgetDetails);
        this.updateVersionVariables(this.selectedVersion);
        this.triggerCanvasUpdate();
      },
      triggerCanvasUpdate() {
        // NOTE: If edit modal is used to update properties or drag/drop in canvas is done then
        // it is NOT necessary to call dragAndDrop.triggerCanvasUpdate(true)
        //
        // Since metaData is updated and no modal was used to update version/properties neither
        // a drag/drop was done on the canvas, then we need to call
        // dragAndDrop.triggerCanvasUpdate(true) to save metaData to content page.
        // It will save to both Vue store and localStore
        dragAndDrop.triggerCanvasUpdate(true);
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  .custom-display-element-config {
    position: relative; /* component must be position relative */
    @include mixin-box;
    padding: 12px;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #080707;
    .header {
      display: grid;
      box-sizing: border-box;
      grid-template-columns: auto 3rem;
    }
    .loader {
      width: 1.5rem;
      height: 1.5rem;
    }
    .main-content {
      display: flex;
      align-items: center;
      gap: 1rem;
      .new-version {
        cursor: pointer;
        svg {
          height: 1rem;
          width: 1rem;
          vertical-align: sub;
        }
      }
      .loader {
        margin: 0;
      }
    }
  }
</style>
